import { useCallback, useEffect, useLayoutEffect, useState, lazy, Suspense } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Globe, MapPin, Sparkles, Feather, Zap, Camera, Video, Map, X, Mail, Clock, Monitor, Calendar, Mic, Settings, Coffee } from 'lucide-react';
import Header from './components/Header';
import Footer from './components/Footer';
import FeatureCard from './components/FeatureCard';
import AppStoreBadges from './components/AppStoreBadges';
import StructuredData from './components/StructuredData';
import ResponsiveImage from './components/ResponsiveImage';
import { LANGUAGE_STORAGE_KEY, rtlLanguages, supportedLanguages } from './i18n/config';

// Lazy load heavy components that are below the fold
const FAQ = lazy(() => import('./components/FAQ'));
const VideoIdeas = lazy(() => import('./components/VideoIdeas'));
const Changelog = lazy(() => import('./components/Changelog'));

const THEME_STORAGE_KEY = 'macsuper-theme';

const getSystemPreference = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialTheme = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  if (typeof window.__INITIAL_THEME__ === 'string') {
    return window.__INITIAL_THEME__;
  }

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch (error) {
    return getSystemPreference();
  }

  return getSystemPreference();
};

function App() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const isRTL = rtlLanguages.includes(i18n.language);
  const [theme, setTheme] = useState(getInitialTheme);
  const [hasManualPreference, setHasManualPreference] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    if (typeof window.__HAS_MANUAL_THEME__ === 'boolean') {
      return window.__HAS_MANUAL_THEME__;
    }

    try {
      const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
      return stored === 'light' || stored === 'dark';
    } catch (error) {
      return false;
    }
  });
  const [showContactPopup, setShowContactPopup] = useState(() => {
    if (typeof window === 'undefined') return false;
    const redirectPath = sessionStorage.getItem('redirectPath');
    const path = window.location.pathname;
    return path === '/contact' || path.endsWith('/contact') || redirectPath === '/contact';
  });
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(() => {
    if (typeof window === 'undefined') return false;
    const redirectPath = sessionStorage.getItem('redirectPath');
    const path = window.location.pathname;
    return path === '/privacy' || path.endsWith('/privacy') || redirectPath === '/privacy';
  });


  // Handle GitHub Pages redirect scenario
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const redirectPath = sessionStorage.getItem('redirectPath');
    if (redirectPath) {
      sessionStorage.removeItem('redirectPath');
      
      if (redirectPath === '/contact') {
        setShowContactPopup(true);
      } else if (redirectPath === '/privacy') {
        setShowPrivacyPopup(true);
      }
      
      // Update the URL without page reload
      window.history.replaceState(null, '', redirectPath);
    }
  }, []);

  // DO NOT change language here - it's already set correctly in main.jsx
  // Changing it here causes flickering from English to target language
  
  // Track current language for re-renders only
  const [currentLang, setCurrentLang] = useState(i18n.language);

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLang(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Add DataFast Analytics script
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check if script is already added
    if (document.querySelector('script[data-website-id="dfid_macsuper_placeholder"]')) {
      return;
    }

    // const script = document.createElement('script');
    // script.defer = true;
    // script.src = 'https://datafa.st/js/script.js';
    // script.setAttribute('data-website-id', 'dfid_macsuper_placeholder');
    // script.setAttribute('data-domain', 'macsuperapp.com');

    //document.head.appendChild(script);

    // Cleanup function
    return () => {
      const existingScript = document.querySelector('script[data-website-id="dfid_macsuper_placeholder"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  // Update document metadata when language changes
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const title = t('meta.title');
    const description = t('meta.description');
    const keywords = t('meta.keywords');

    // Update title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (selector, content) => {
      const element = document.querySelector(selector);
      if (element) {
        element.setAttribute('content', content);
      }
    };

    updateMetaTag('meta[name="title"]', title);
    updateMetaTag('meta[name="description"]', description);
    updateMetaTag('meta[name="keywords"]', keywords);
    updateMetaTag('meta[property="og:title"]', title);
    updateMetaTag('meta[property="og:description"]', description);
    updateMetaTag('meta[property="twitter:title"]', title);
    updateMetaTag('meta[property="twitter:description"]', description);
  }, [t, i18n.language, currentLang]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';

    if (typeof window !== 'undefined') {
      window.__INITIAL_THEME__ = theme;
    }
  }, [theme, isRTL]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.__HAS_MANUAL_THEME__ = hasManualPreference;

    try {
      if (hasManualPreference) {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
      } else {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
      }
    } catch (error) {
      return;
    }
  }, [theme, hasManualPreference]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const syncThemeWithSystem = (event) => {
      if (hasManualPreference) {
        return;
      }
      setTheme(event.matches ? 'dark' : 'light');
    };

    if (!hasManualPreference) {
      setTheme(mediaQuery.matches ? 'dark' : 'light');
    }

    mediaQuery.addEventListener('change', syncThemeWithSystem);

    return () => mediaQuery.removeEventListener('change', syncThemeWithSystem);
  }, [hasManualPreference]);


  const handleToggleTheme = useCallback((event) => {
    if (typeof window !== 'undefined' && typeof window.checkCookieConsent === 'function') {
      if (!window.checkCookieConsent()) {
        if (typeof window.requestCookieConsentForTheme === 'function') {
          window.requestCookieConsentForTheme();
          
          window.onCookieConsentAccepted = () => {
            handleToggleTheme(event);
            window.onCookieConsentAccepted = null;
          };
        }
        return;
      }
    }

    const applyThemeChange = () => {
      flushSync(() => {
        setTheme((current) => {
          const nextTheme = current === 'dark' ? 'light' : 'dark';
          const systemTheme = getSystemPreference();

          if (nextTheme === systemTheme) {
            setHasManualPreference(false);
          } else {
            setHasManualPreference(true);
          }

          if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
            window.gtag('event', 'theme_toggle', {
              event_category: 'user_preference',
              event_label: nextTheme,
              is_system_preference: nextTheme === systemTheme
            });
          }

          return nextTheme;
        });
      });
    };

    if (!document.startViewTransition || !event) {
      applyThemeChange();
      return;
    }

    const x = event.clientX;
    const y = event.clientY;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    const transition = document.startViewTransition(applyThemeChange);

    transition.ready.then(() => {
      const clipPath = [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${endRadius}px at ${x}px ${y}px)`,
      ];

      document.documentElement.animate(
        {
          clipPath: clipPath,
        },
        {
          duration: 500,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      );
    });
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.resetThemeToSystem = () => {
        const systemTheme = getSystemPreference();
        setTheme(systemTheme);
        setHasManualPreference(false);
      };
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        window.resetThemeToSystem = null;
      }
    };
  }, []);

  const openContactPopup = () => {
    setShowContactPopup(true);
    const basePath = i18n.language === 'ro' ? '/ro' : '';
    window.history.pushState(null, '', `${basePath}/contact`);
  };
  
  const openPrivacyPopup = () => {
    setShowPrivacyPopup(true);
    const basePath = i18n.language === 'ro' ? '/ro' : '';
    window.history.pushState(null, '', `${basePath}/privacy`);
  };

  const features = [
    {
      icon: Clock,
      screenshot: '/app_screenshots/pomodoro.webp',
      title: t('features.pomodoroTimer.title'),
      description: t('features.pomodoroTimer.description')
    },
    {
      icon: Calendar,
      screenshot: '/app_screenshots/calendar.webp',
      title: t('features.quickMiniCalendar.title'),
      description: t('features.quickMiniCalendar.description')
    },
    {
      icon: Coffee,
      screenshot: '/app_screenshots/keep_awake.webp', 
      title: t('features.keepAwake.title'),
      description: t('features.keepAwake.description')
    },
    {
      icon: Camera,
      screenshot: '/app_screenshots/camera.webp',
      title: t('features.cameraPreview.title'), 
      description: t('features.cameraPreview.description')
    },
    {
      icon: Mic,
      screenshot: '/app_screenshots/microphone.gif',
      title: t('features.micMuteUnmute.title'),
      description: t('features.micMuteUnmute.description')
    },
    {
      icon: Settings,
      screenshot: '/app_screenshots/main_app.webp',
      title: t('features.quickMacSettings.title'),
      description: t('features.quickMacSettings.description')
    }
  ];

  const ContactPopup = ({ isOpen, onClose }) => {
    useEffect(() => {
      if (isOpen) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
          const header = document.querySelector('header');
          if (header) header.style.paddingRight = `${scrollbarWidth}px`;
        }
      } else {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      }
      return () => {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
        onClick={onClose}
      >
        <div
          className="bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl rounded-3xl shadow-2xl max-w-md w-full p-4 border-2 border-sky-200/50 dark:border-sky-700/50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-sky-600 to-blue-600 rounded-2xl mb-4 p-4 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white drop-shadow-sm">
              {t('contact.title')}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200 hover:scale-105"
              aria-label={t('contact.close')}
            >
              <X size={24} />
            </button>
          </div>
          <div className="text-center mb-4 mt-8">
            <a 
              href="mailto:contact@qod.ee?subject=TripReel%20Suggestion" 
              className="inline-flex items-center gap-2 text-lg font-semibold text-sky-700 dark:text-sky-300 hover:text-sky-800 dark:hover:text-sky-200 transition-colors"
            >
              <Mail size={20} />
              contact@qod.ee
            </a>
          </div>
          <p className="text-slate-700 dark:text-slate-300 text-center">
            {t('contact.description')}
          </p>
        </div>
      </div>
    );
  };

  const PrivacyPopup = ({ isOpen, onClose }) => {
    useEffect(() => {
      if (isOpen) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
          const header = document.querySelector('header');
          if (header) header.style.paddingRight = `${scrollbarWidth}px`;
        }
      } else {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      }
      return () => {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
        onClick={onClose}
      >
        <div
          className="bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden border-2 border-sky-200/50 dark:border-sky-700/50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-sky-600 to-blue-600 rounded-2xl mx-4 my-4 p-4 flex items-center justify-between sticky top-0 z-10">
            <h3 className="text-xl font-bold text-white drop-shadow-sm">
              App Privacy Policy
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200 hover:scale-105"
              aria-label={t('contact.close')}
            >
              <X size={24} />
            </button>
          </div>
          <div className="overflow-y-auto max-h-[calc(85vh-8rem)] px-6 pb-6">
            <div className="prose prose-sm sm:prose dark:prose-invert max-w-none pt-6">
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                <strong>Last updated:</strong> December 31, 2025
              </p>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">1. Introduction</h3>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                This Privacy Policy applies to SuperMenuBar (the "App"), a Mac menu bar utility that provides productivity tools. Your privacy is our top priority. This policy explains how information is handled when you use the App.
              </p>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">2. Data Collection and Use</h3>
              <div className="bg-sky-500/20 dark:bg-slate-700/50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">🍃 No Data Collection</h4>
                <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">
                  SuperMenuBar is designed with privacy in mind:
                </p>
                <ul className={`list-disc ${isRTL ? 'pr-6' : 'pl-6'} text-slate-700 dark:text-slate-300 text-sm space-y-1`}>
                  <li>All settings and preferences are stored locally on your Mac</li>
                  <li>No data is transmitted to external servers</li>
                  <li>No analytics, tracking, or telemetry</li>
                  <li>No network connections required for core functionality</li>
                </ul>
              </div>

              <div className="bg-green-500/20 dark:bg-green-900/20 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">What We Do NOT Collect</h4>
                <ul className="list-none text-slate-700 dark:text-slate-300 text-sm space-y-2">
                  <li>✅ No personal information such as names, email addresses, or any other identifying information</li>
                  <li>✅ No user accounts or registration required</li>
                  <li>✅ No tracking of individual user behavior</li>
                  <li>✅ No sharing of data with third parties</li>
                  <li>✅ No access to files, documents, or personal data</li>
                </ul>
              </div>

              <div className="bg-amber-500/20 dark:bg-amber-900/20 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">🔒 Local Storage Only</h4>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  All SuperMenuBar settings (Pomodoro preferences, Keep Awake durations, calendar settings, etc.) are stored locally in your Mac's application support folders and never leave your device.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">3. Permissions</h3>
              <div className="bg-blue-500/20 dark:bg-blue-900/20 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">🎥 Camera Access</h4>
                <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">
                  SuperMenuBar requests camera access only for the Live Camera Preview feature. This allows you to view your camera feed locally - no recording, storage, or transmission occurs.
                </p>
                
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 mt-4">🎤 Microphone Access</h4>
                <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">
                  Microphone access is used only for audio level monitoring and mute/unmute functionality. No audio is recorded, stored, or transmitted.
                </p>

                {/* <h4 className="font-semibold text-slate-900 dark:text-white mb-2 mt-4">📅 Calendar Access</h4>
                <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">
                  Calendar access allows the Quick Calendar feature to display your events. All calendar data access is read-only and local to your Mac.
                </p> */}

                <p className="text-slate-700 dark:text-slate-300 text-sm mt-4">
                  <strong>What We Do NOT Access:</strong>
                </p>
                <ul className="list-none text-slate-700 dark:text-slate-300 text-sm space-y-1 mt-1">
                  <li>❌ No recording or storage of camera/microphone data</li>
                  <li>❌ No modification of calendar events</li>
                  <li>❌ No access to other apps or system data beyond permissions requested</li>
                  <li>❌ Contacts, messages, or other personal information</li>
                </ul>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">Contact</h3>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                Questions about this privacy policy? <button onClick={() => { onClose(); openContactPopup(); }} className="text-sky-700 dark:text-sky-300 hover:underline">Contact page</button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <Header theme={theme} onToggleTheme={handleToggleTheme} />
      
      {/* Hero Section */}
      <section className="pt-16 sm:pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {/* Badge - hide on mobile */}
              <div className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-sky-100 dark:bg-sky-900/30 rounded-full text-sky-700 dark:text-sky-300 text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>{t('hero.badge')}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight text-balance">
                <span className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-600 dark:from-sky-400 dark:via-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                  {t('hero.title1')}
                </span>
                <br />
                <span className="text-slate-900 dark:text-white">
                  {t('hero.title2')}
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl">
                {t('hero.description')}
              </p>
              
              <div className="flex gap-3 sm:gap-4 items-center">
                <AppStoreBadges />
                
                {/* Feature indicators - next to download button */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 sm:gap-2 text-sm sm:text-md text-slate-600 dark:text-slate-400">
                    <Feather className="w-3 h-3 sm:w-12 sm:h-6 text-green-600 dark:text-green-400" />
                    <span>{t('hero.secure')}</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 text-sm sm:text-md text-slate-600 dark:text-slate-400">
                    <Zap className="w-3 h-3 sm:w-12 sm:h-6 text-amber-600 dark:text-amber-400" />
                    <span>{t('hero.worksOffline')}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <video 
                className="w-full h-auto floating-video-demo rounded-2xl shadow-2xl"
                autoPlay
                muted
                loop
                playsInline
                poster="/app_screenshots/main_app.webp"
              >
                <source src="/app_screenshots/video_demo.webm" type="video/webm" />
                <source src="/app_screenshots/video_demo.mp4" type="video/mp4" />
                <source src="/app_screenshots/video_demo.mov" type="video/quicktime" />
                Your browser does not support the video tag.
              </video>
              
              {/* Mobile-only badge below video */}
              <div className="sm:hidden mt-6 flex justify-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 dark:bg-sky-900/30 rounded-full text-sky-700 dark:text-sky-300 text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  <span>{t('hero.badge')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Bar Integration Section */}
      <section id="how-it-works" className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-100 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              {t('howItWorks.title')}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              {t('howItWorks.description')}
            </p>
          </div>
          
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-4xl mx-auto">
            <div className="bg-slate-100 dark:bg-slate-700 rounded-xl p-4 overflow-hidden">
              <div className="relative" style={{marginTop: '-16px', marginLeft: '-16px', marginRight: '-16px'}}>
                <img 
                  src="/app_screenshots/menubar.webp" 
                  alt={t('images.menuBarIntegration')}
                  className="w-full h-auto"
                  style={{ maxHeight: '30px', objectFit: 'contain', background: "rgb(46 120 177)", objectPosition: "right" }}
                />
                {/* Apple logo overlay - hidden on small screens */}
                <div 
                  className="absolute top-0 left-1 flex items-center justify-center hidden sm:flex"
                  style={{ 
                    width: '30px', 
                    height: '30px',
                    fontSize: '20px',
                    color: '#ffffff',
                    zIndex: 10
                  }}
                >
                  &#63743;
                </div>
                
                {/* Border around middle of menubar */}
                <div 
                  className="absolute top-0 border-2 border-sky-500 border-dashed rounded"
                  style={{
                    right: '218px',
                    width: '182px',
                    height: '100%',
                    zIndex: 15
                  }}
                ></div>
                
                {/* Arrow pointing from border to main app */}
                <div className="absolute z-20" style={{
                  right: '146px',
                  top: '100%',
                  width: '182px',
                  height: '30px',
                }}>
                  {/* Curved arrow */}
                  <svg width="100%" height="100%" viewBox="0 0 182 50" className="overflow-visible">
                    <defs>
                      <marker id="arrowhead-better" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth">
                        <path d="M2,2 L2,10 L10,6 z" fill="#0ea5e9" stroke="#0ea5e9" strokeWidth="1"/>
                      </marker>
                    </defs>
                    <path 
                      d="M91,0 Q91,25 45,45" 
                      stroke="#0ea5e9" 
                      strokeWidth="3" 
                      fill="none" 
                      markerEnd="url(#arrowhead-better)"
                      strokeDasharray="8,5"
                    />
                  </svg>
                </div>
              </div>
              <img 
                src="/app_screenshots/main_app.webp" 
                alt="SuperMenuBar Menu Bar Integration"
                className="mt-4 w-full h-auto relative z-10"
                style={{ maxHeight: '420px', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              {t('features.title')}
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300 flex flex-col h-full">
                <div className="p-6 flex flex-col flex-1">
                  {/* Header with icon only */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">{feature.title}</h3>
                    </div>
                  </div>
                  
                  {/* Screenshot - centered vertically */}
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-xl p-4 mb-4 flex-1 flex items-center justify-center min-h-[250px]">
                    <img 
                      src={feature.screenshot}
                      alt={feature.title}
                      className="max-w-full max-h-full object-contain rounded-lg"
                    />
                  </div>
                  
                  {/* Description - aligned to bottom */}
                  <div className="mt-auto">
                    <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <Suspense fallback={<div className="py-20 text-center"><div className="inline-block w-8 h-8 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin"></div></div>}>
        <FAQ />
      </Suspense>

      {/* Changelog Section */}
      <Suspense fallback={<div className="py-20 text-center"><div className="inline-block w-8 h-8 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin"></div></div>}>
        <Changelog />
      </Suspense>

      {/* Video Ideas Section */}
      <Suspense fallback={<div className="py-20 text-center"><div className="inline-block w-8 h-8 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin"></div></div>}>
        <VideoIdeas />
      </Suspense>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-100 dark:bg-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              {t('about.title')}
            </h2>
            <p className="text-xl text-slate-600 dark:text-sky-400 font-semibold">
              {t('about.subtitle')}
            </p>
          </div>

          {/* How It Works */}
          <div className="mb-16 bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {t('about.howItWorks.title')}
            </h3>
            <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
              {t('about.howItWorks.description')}
            </p>
          </div>

          {/* Customization */}
          <div className="mb-16 bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {t('about.customization.title')}
            </h3>
            <p className="text-slate-700 dark:text-slate-300 mb-6">
              {t('about.customization.description')}
            </p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.videoQuality')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.bitrate')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.earthStyles')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.borders')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.aspectRatios')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.tripSpeed')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.pauseDuration')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.locationLabels')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.pathDisplay')}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-600 dark:from-sky-600 dark:via-blue-700 dark:to-indigo-700 rounded-3xl p-12 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500/10 to-blue-600/10 opacity-20"></div>
            
            <div className="relative z-10 text-center space-y-8">
              <h2 className="text-4xl sm:text-5xl font-bold text-white">
                {t('cta.title')}
              </h2>
              <p className="text-xl text-blue-50 max-w-2xl mx-auto">
                {t('cta.description')}
              </p>
              
              <div className="pt-4">
                <AppStoreBadges />
              </div>
              
              <p className="text-sm text-blue-100 pt-4">
                {t('cta.features')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer openContactPopup={openContactPopup} openPrivacyPopup={openPrivacyPopup} />

      <ContactPopup
        isOpen={showContactPopup}
        onClose={() => {
          setShowContactPopup(false);
          const basePath = i18n.language === 'ro' ? '/ro' : '';
          window.history.pushState(null, '', basePath || '/');
        }}
      />

      <PrivacyPopup
        isOpen={showPrivacyPopup}
        onClose={() => {
          setShowPrivacyPopup(false);
          const basePath = i18n.language === 'ro' ? '/ro' : '';
          window.history.pushState(null, '', basePath || '/');
        }}
      />
      <StructuredData />
    </div>
  );
}

export default App;
